import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';
import { supabase } from '../../lib/supabase';

interface Realisatie {
  id: number;
  image_url: string;
  created_at: string;
}

export default function RealisatiesPage() {
  const [realisaties, setRealisaties] = useState<Realisatie[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(12);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://dsfcartech.be';

  useEffect(() => {
    fetchRealisaties();
  }, []);

  const fetchRealisaties = async () => {
    try {
      const { data, error } = await supabase
        .from('realisaties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRealisaties(data || []);
    } catch (error) {
      console.error('Error fetching realisaties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setVisibleCount(prev => prev + 12);
  };

  const visibleRealisaties = realisaties.slice(0, visibleCount);
  const hasMore = visibleCount < realisaties.length;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Realisaties - Auto Upgrades Antwerpen | DSF Cartech",
    "description": "Bekijk onze professionele auto-upgrades: CarPlay, Android Auto, sterrenhemel, sfeerverlichting en meer. Echte projecten in Antwerpen.",
    "url": `${siteUrl}/realisaties`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "DSF Cartech",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Ternesselei 191",
        "addressLocality": "Wommelgem",
        "addressRegion": "Antwerpen",
        "postalCode": "2160",
        "addressCountry": "BE"
      }
    }
  };

  return (
    <>
      <SEOHead
        title="Realisaties - Auto Upgrades Antwerpen | DSF Cartech"
        description="Bekijk onze professionele auto-upgrades: CarPlay, Android Auto, sterrenhemel, sfeerverlichting, achteruitrijcamera's en Android schermen. Echte projecten in Antwerpen."
        keywords="auto upgrades realisaties, carplay installatie voorbeelden, sterrenhemel auto, sfeerverlichting auto, achteruitrijcamera installatie, android scherm auto, auto elektronica antwerpen"
        canonical={`${siteUrl}/realisaties`}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Onze Realisaties
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bekijk de transformaties die we hebben gerealiseerd voor onze klanten
            </p>
          </div>
        </section>

        {/* Photo Grid */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            {loading && (
              <div className="text-center py-20">
                <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Foto's laden...</p>
              </div>
            )}

            {!loading && realisaties.length > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {visibleRealisaties.map((realisatie, index) => (
                    <div
                      key={realisatie.id}
                      onClick={() => setSelectedImage(realisatie.image_url)}
                      className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md aspect-square bg-gray-100"
                    >
                      <img
                        src={realisatie.image_url}
                        alt={`DSF Cartech realisatie ${realisatie.id} - Auto upgrade Antwerpen`}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                        loading={index < 4 ? 'eager' : 'lazy'}
                        decoding="async"
                        fetchpriority={index < 4 ? 'high' : 'auto'}
                      />
                      
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center">
                        <i className="ri-zoom-in-line text-3xl text-white"></i>
                      </div>
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className="text-center mt-12">
                    <button
                      onClick={loadMore}
                      className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Meer foto's laden ({realisaties.length - visibleCount} nog)
                    </button>
                  </div>
                )}
              </>
            )}

            {!loading && realisaties.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-image-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg">Nog geen foto's toegevoegd</p>
              </div>
            )}
          </div>
        </section>

        {selectedImage && (
          <div
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 cursor-zoom-out"
            style={{ animation: 'fadeIn 0.15s ease-out' }}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white z-10 cursor-pointer transition-all duration-150 backdrop-blur-sm"
              aria-label="Sluiten"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div
              className="max-w-6xl max-h-[90vh] relative"
              onClick={(e) => e.stopPropagation()}
              style={{ animation: 'zoomIn 0.2s ease-out' }}
            >
              <img
                src={selectedImage}
                alt="DSF Cartech realisatie detail"
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}

        <Footer />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
}
